import { getSession } from 'next-auth/react'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

// Circuit breaker: track consecutive 401s globally to avoid infinite loops.
// After MAX_401_BEFORE_REDIRECT consecutive failures, do a soft redirect to
// the sign-in page instead of repeatedly hitting the backend.
let consecutive401Count = 0
const MAX_401_BEFORE_REDIRECT = 3

/**
 * A robust API client that handles authentication and base path resolution.
 *
 * IMPORTANT: This function never calls signIn() directly. Triggering a
 * full-page redirect from an API utility causes infinite redirect loops
 * when the backend persistently returns 401. Session expiry is handled
 * at the component level by useSessionRefresh().
 *
 * @param endpoint The API endpoint to call, e.g., '/api/test'.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // NFR-03: Attach correlation ID for end-to-end tracing
  if (!headers.has('X-APCC-Request-Id')) {
    headers.set('X-APCC-Request-Id', crypto.randomUUID())
  }

  // Handle session errors that occurred during a token refresh attempt.
  // Don't call signIn() here — useSessionRefresh() handles re-auth at
  // the component level to avoid redirect loops.
  if (session?.error === 'RefreshAccessTokenError') {
    const err = new Error('Session expired. Please sign in again.')
    err.name = 'AuthenticationError'
    throw err
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, throw a typed error instead of calling
  // signIn(). After MAX_401_BEFORE_REDIRECT consecutive 401s, do a soft
  // redirect to the sign-in page (not Keycloak directly, to avoid loops).
  if (response.status === 401) {
    consecutive401Count++
    if (consecutive401Count >= MAX_401_BEFORE_REDIRECT && typeof window !== 'undefined') {
      consecutive401Count = 0
      window.location.href = '/auth/signin'
      // Return a never-resolving promise — the page is navigating away
      return new Promise<T>(() => {})
    }
    const err = new Error('Your session has expired. Please sign in again.')
    err.name = 'AuthenticationError'
    throw err
  }

  // Any successful response resets the 401 counter
  consecutive401Count = 0

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new Error(errorData.detail || 'An API error occurred.')
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a file upload (multipart/form-data).
   * Do NOT set Content-Type — the browser will auto-set it with the boundary.
   */
  upload: <T = unknown>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
      // No Content-Type header — browser sets multipart boundary automatically
    })
  },

  /**
   * Perform a file upload with progress tracking via XMLHttpRequest.
   * Use this for large file uploads (e.g., ZIP archives) where upload progress matters.
   */
  uploadWithProgress: <T = unknown>(
    endpoint: string,
    formData: FormData,
    onProgress?: (pct: number, loaded: number, total: number) => void,
  ): Promise<T> => {
    return new Promise(async (resolve, reject) => {
      const session = await getSession()

      const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

      const xhr = new XMLHttpRequest()
      xhr.open('POST', fullUrl)

      // Set auth header
      if (session?.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${session.accessToken}`)
      }
      xhr.setRequestHeader('X-APCC-Request-Id', crypto.randomUUID())

      // Track upload progress
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100)
            onProgress(pct, e.loaded, e.total)
          }
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as T)
          } catch {
            resolve(null as T)
          }
        } else if (xhr.status === 401) {
          reject(new Error('Your session has expired. Please sign in again.'))
        } else {
          try {
            const err = JSON.parse(xhr.responseText)
            reject(new Error(err.detail || 'Upload failed'))
          } catch {
            reject(new Error(`Upload failed (HTTP ${xhr.status})`))
          }
        }
      }

      xhr.onerror = () => reject(new Error('Network error during upload'))
      xhr.ontimeout = () => reject(new Error('Upload timed out'))

      // No timeout for large uploads
      xhr.timeout = 0

      xhr.send(formData)
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

// Default export for backward compatibility
export default apiClientFetch
